import { NodeModel, RenderParams } from "@minoru/react-dnd-treeview";
import { ArrowRight, Folder, InsertChart } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import React, { useCallback, useEffect } from "react";
import { TagFromTree } from "../models/TrendingModelsV2";

interface CustomNodeProps {
	node: NodeModel<TagFromTree>;
	setContextMenuOver: (
		data: NodeModel<TagFromTree>,
		e: React.MouseEvent<HTMLElement>,
	) => void;
	options: RenderParams;
	openTagFolders: {
		[TagId: string]: string | number;
		[TagId: number]: string | number;
	};
	handleSelect: (node: NodeModel<TagFromTree>) => void;
	setOpenTagFolders: React.Dispatch<
		React.SetStateAction<{
			[TagId: string]: string | number;
			[TagId: number]: string | number;
		}>
	>;
	isSelected: boolean;
}

const customStyles = `
  ul, ol {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }
`;

export const TagFolderCustomNodeV2: React.FC<CustomNodeProps> = ({
	node,
	options: { depth, isOpen, onToggle, containerRef },
	options,
	setOpenTagFolders,
	openTagFolders,
	handleSelect,
	setContextMenuOver,
	isSelected,
}) => {
	const onClickHandler = () => {
		if (options.hasChild || node.data?.TagTypeCode === "F") {
			if (openTagFolders[node.id]) {
				const newOpenTagFolders = { ...openTagFolders };
				delete newOpenTagFolders[node.id as number];
				setOpenTagFolders(newOpenTagFolders);
			} else {
				setOpenTagFolders({ ...openTagFolders, [node.id]: node.id });
			}
			onToggle();
			return;
		}
		if (!options.hasChild) {
			handleSelect(node);
		}
	};

	const handleContextMenu = useCallback(
		(ev: any) => {
			ev.stopPropagation();
			ev.preventDefault();
			setContextMenuOver(node, ev);
		},
		[node.id, setContextMenuOver],
	);

	useEffect(() => {
		containerRef.current?.addEventListener("contextmenu", handleContextMenu);

		return () => {
			containerRef.current?.removeEventListener(
				"contextmenu",
				handleContextMenu,
			);
		};
	}, []);

	return (
		<Box
			data-node-id={node.id}
			onClick={onClickHandler}
			sx={{
				paddingInlineStart: depth * 2,
				cursor: "pointer",
				alignItems: "center",
				display: "flex",
				height: "32px",
				backgroundColor: isSelected ? "#e8f0fe" : "",
				"&:hover": {
					backgroundColor: "#f2f2f2",
				},
			}}
		>
			<style>{customStyles}</style>
			{
				<>
					{options.hasChild ? (
						<div>{isOpen ? <ArrowDropDownIcon /> : <ArrowRight />}</div>
					) : null}

					<div>
						{options.hasChild || node.data?.TagTypeCode === "F" ? (
							<Folder />
						) : (
							<InsertChart fontSize="small" />
						)}
					</div>
				</>
			}
			{node.data?.TagName && (
				<>
					{node.data?.TagTypeCode && node.data?.TagTypeCode !== "F" && (
						<Box
							sx={{
								width: "20px",
								height: "20px",
								color: "white",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								fontSize: "10px",
								fontWeight: "bold",
								borderRadius: "3px",
								border: "1px solid rgba(0, 0, 0, 0.2)",
								backgroundColor:
									node.data?.TagTypeCode === "A" ? "#AD48C4" : "#C46F48",
							}}
						>
							{node.data?.TagTypeCode}
						</Box>
					)}
					<Tooltip title={node.data.TagName} placement="top" arrow>
						<Typography variant="body2">
							{node.data?.Alias || node.data?.TagName}
						</Typography>
					</Tooltip>
				</>
			)}
		</Box>
	);
};
